import { BrowserUtility } from '@utility/browser-utility';
import { CommonUtility } from '@utility/common';
import {
  CommonConstant,
  ErrorMessageConstant,
} from '@utility/constants/common';
import { ComponentsService } from '@utility/services/components';
import { useCallback, useEffect, useState } from 'react';
import { convertItemToImperial } from '@page-components/shipment';
import { useAuth } from '@contexts/auth';
import { useDebouncedEffect } from './debounceAsync';

export const GetComponentDetailsHook = id => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError('');
        const result = await ComponentsService.getById(id);
        setData(result.data);
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchData();
    }
  }, [id, refresh]);

  const refreshData = () => {
    setRefresh(Math.random());
  };

  return {
    data,
    loading,
    error,
    refresh,
    refreshData,
    setData,
  };
};

export const GetComponentItemsHook = componentId => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refresh, setRefresh] = useState(0);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');
      const result = await ComponentsService.items(componentId, true);
      setData(result.data || []);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (componentId) {
      fetchData();
    }
  }, [componentId, refresh]);

  const refreshData = () => {
    setRefresh(Math.random());
  };

  return {
    data,
    loading,
    error,
    setData,
    refreshData,
  };
};

export const GetComponentLogsHook = componentId => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refresh, setRefresh] = useState(0);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');
      const result = await ComponentsService.getLogs(componentId);
      setData(result.data || []);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (componentId && refresh) {
      fetchData();
    }
  }, [componentId, refresh]);

  const refreshData = () => {
    setRefresh(Math.random());
  };

  return {
    data,
    loading,
    error,
    setData,
    refreshData,
  };
};

export const GetSupplierComponentsHook = supplierId => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [totalPages, setTotalPages] = useState(0);
  const [totalData, setTotalData] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);

  const [filter, setFilter] = useState({
    limit: CommonConstant.defaultPageSize,
    page: 1,
    search: '',
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const reqParams = {
        ...filter,
        search: filter.search.trim(),
      };
      const result = await ComponentsService.supplierWise(
        supplierId,
        reqParams,
      );
      result.data = result.data.sort(
        (a, b) => (b.predictedQty || 0) - (a.predictedQty || 0),
      );
      setData(result.data);
      setTotalPages(result.total_pages);
      setTotalData(result.total_data);
    } catch (error) {
      setError(error.error || ErrorMessageConstant.defaultAPIError);
    } finally {
      setLoading(false);
    }
  };

  useDebouncedEffect(
    () => {
      if (supplierId) {
        fetchData();
      }
    },
    [supplierId, filter, refreshKey],
    500,
  );

  const refresh = () => {
    setRefreshKey(refreshKey => refreshKey + 1);
  };

  const pageChanged = page => {
    const temp = {
      ...filter,
      page,
    };
    setFilter({ ...temp });
  };

  const filterChanged = (key, value) => {
    const temp = {
      ...filter,
      page: 1,
      [key]: value,
    };
    setFilter({ ...temp });
  };

  const setSearch = search => {
    filterChanged('search', search);
  };

  return {
    data,
    setData,
    loading,
    error,
    totalPages,
    filterChanged,
    pageChanged,
    filter,
    totalData,
    search: filter.search,
    setSearch,
    refresh,
  };
};

export const GetPurchaseOrderComponentsHook = (
  supplierId,
  imperial,
  componentQty = {},
) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [totalPages, setTotalPages] = useState(0);
  const [totalData, setTotalData] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);

  const [filter, setFilter] = useState({
    limit: 50,
    page: 1,
    search: '',
  });

  const refresh = () => {
    setRefreshKey(Math.random());
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const reqParams = {
        ...filter,
        search: filter.search.trim(),
      };
      const result = await ComponentsService.supplierWise(
        supplierId,
        reqParams,
      );
      const temp = result.data.map(item => {
        const t = { ...item };
        t.prices = [...(t.prices || [])].sort((a, b) => a.moq - b.moq);
        t.attributes = [];
        t.currentAttributes = [];
        if ((item.attributes || []).length > 0) {
          let temp = CommonUtility.getLatestAttribute(item);
          t.attributes = temp ? [temp] : [];
          if (componentQty[item._id]) {
            const keyData = Object.values(componentQty[item._id].aliases || {});
            const tempUPCItems = item.attributes.filter(x =>
              // @ts-expect-error - TS18046: 'y' is of type 'unknown'.
              keyData.some(y => y.alias === x.alias && y.ctn_qty > 0),
            );
            temp = [...tempUPCItems];
            if (tempUPCItems.length > 0) {
              t.attributes = [...tempUPCItems];
            }
          }
          t.currentAttributes = [...(item.attributes || [])];
        }

        return t;
      });
      let _items = [];
      if (refreshKey > 0 && imperial) {
        _items = temp.map(item => {
          item.attributes = item?.attributes?.map(att =>
            convertItemToImperial(att),
          );
          return item;
        });
      } else {
        _items = temp;
      }

      setData(
        // @ts-expect-error - TS2339: Property 'predictedQty' does not exist on type 'never'.
        _items.sort((a, b) => (b.predictedQty || 0) - (a.predictedQty || 0)),
      );
      setTotalPages(result.total_pages);
      setTotalData(result.total_data);
    } catch (error) {
      setError(error.error || ErrorMessageConstant.defaultAPIError);
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 100);
    }
  };

  useDebouncedEffect(
    () => {
      if (supplierId) {
        fetchData();
      }
    },
    [supplierId, filter, refreshKey],
    500,
  );

  const pageChanged = page => {
    const temp = {
      ...filter,
      page,
    };
    setFilter({ ...temp });
  };

  const filterChanged = (key, value) => {
    const temp = {
      ...filter,
      page: 1,
      [key]: value,
    };
    setFilter({ ...temp });
  };

  const setSearch = search => {
    filterChanged('search', search);
  };

  return {
    data,
    setData,
    loading,
    error,
    totalPages,
    filterChanged,
    pageChanged,
    filter,
    totalData,
    refresh,
    search: filter.search,
    setSearch,
  };
};

const Unit = {
  mL: 1000,
  'fl oz': 153.722,
  g: 1000,
  oz: 16,
};

export const useComponentsMetadata = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const { wmsPortal, isAuthenticated } = useAuth();

  const fetchData = useCallback(async () => {
    const existing = BrowserUtility.getObj(CommonConstant.componentsMetadata);

    if (CommonUtility.isValidArray(existing)) {
      // @ts-expect-error - TS2345: Argument of type 'unknown[]' is not assignable to parameter of type 'SetStateAct...
      setData(existing);
      return true;
    }

    try {
      setLoading(true);
      const res = await ComponentsService.fetchComponentsMetadata();
      const units = (res.data?.units || []).map(item => {
        const tempClass = {
          ...item,
        };
        switch (item.value) {
          case 'volume':
            tempClass.baseMetricUnit = 'mL';
            tempClass.baseImperialUnit = 'fl oz';
            break;

          case 'weight':
            tempClass.baseMetricUnit = 'g';
            tempClass.baseImperialUnit = 'oz';
        }
        if (tempClass.baseImperialUnit) {
          tempClass.units = item.units.map(unit => ({
            ...unit,
            metricBaseConversion:
              unit.metric === tempClass.baseMetricUnit
                ? 1
                : Unit[tempClass.baseMetricUnit],
            imperialBaseConversion:
              unit.imperial === tempClass.baseImperialUnit
                ? 1
                : Unit[tempClass.baseImperialUnit],
          }));
        }
        return tempClass;
      });

      BrowserUtility.saveObj(CommonConstant.componentsMetadata, units);
      setData(units);
    } catch (error) {
      // console.log("error", error)
    } finally {
      setLoading(false);
    }
  }, []);

  const refresh = fetchData;

  useEffect(() => {
    if (!wmsPortal && isAuthenticated) fetchData();
  }, [wmsPortal, isAuthenticated]);

  const options = data.map(d => ({
    // @ts-expect-error - TS2339: Property 'value' does not exist on type 'never'.
    key: d.value,
    // @ts-expect-error - TS2339: Property 'name' does not exist on type 'never'.
    text: d.name,
    // @ts-expect-error - TS2339: Property 'value' does not exist on type 'never'.
    value: d.value,
    // @ts-expect-error - TS2339: Property 'units' does not exist on type 'never'.
    units: CommonUtility.isValidArray(d?.units)
      ? // @ts-expect-error - TS2339: Property 'units' does not exist on type 'never'.
        d.units.map(c => ({
          ...c,
          text: c.key,
          value: c.key,
        }))
      : [],
  }));

  return {
    loading,
    data,
    options,
    refresh,
  };
};
